import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Overlay } from '~/ui/overlay/Overlay';
import { Modal } from '~/ui/modal/Modal';
import {
  useEditorStore,
  EditorChapter,
  EditorSequence,
  InternalSequenceSettings as Settings
} from '~/shell/Store';

const Panel = styled.div`
  box-sizing: border-box;
  padding: 24px;
  max-height: calc(100vh - 48px);
  overflow: auto;
  font-size: 14px;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }

  .sequence-id,
  small {
    color: #666;
    font-size: 12px;
    line-height: 1.5;
  }

  .sequence-id {
    margin-top: 6px;
    user-select: text;
  }
  .field {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }
  .actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  details {
    border-top: 1px solid #eee;
    padding-top: 12px;
  }
  summary {
    cursor: pointer;
  }
  .detail-content {
    display: grid;
    gap: 8px;
    margin-top: 12px;
  }
  .tag {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    overflow-wrap: anywhere;
  }
  .tag button {
    padding: 4px 8px;
  }

  fieldset {
    border: 0;
    padding: 0;
    margin: 16px 0 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  label {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  select,
  input:not([type='checkbox']),
  button {
    font: inherit;
    padding: 8px 12px;
    border: 1px solid #ccc;
    border-radius: 6px;
    background: #fff;
  }

  button[type='submit'] {
    background: #7c3aed;
    color: #fff;
    border-color: #7c3aed;
  }

  button:disabled {
    opacity: 0.5;
  }

  p {
    margin-top: 12px;
    line-height: 1.5;
  }
`;

const SettingsForm = ({
  sequence,
  onClose
}: {
  sequence: EditorSequence & { internalSettings: Settings };
  onClose: () => void;
}) => {
  const [draft, setDraft] = useState(sequence.internalSettings);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [needsReload, setNeedsReload] = useState(false);
  const [newTag, setNewTag] = useState('');
  const { apiService, training } = useEditorStore();

  useEffect(() => {
    setDraft(sequence.internalSettings);
  }, [sequence.internalSettings]);

  const changed =
    JSON.stringify(draft) !== JSON.stringify(sequence.internalSettings);

  const change = (values: Partial<Settings>) => {
    setDraft(current => ({ ...current, ...values }));
    setSaved(false);
    setNeedsReload(false);
    setError('');
  };

  const addTag = () => {
    const label = newTag.trim();
    if (!label) return;
    change({
      tags: draft.tags.includes(label) ? draft.tags : [...draft.tags, label]
    });
    setNewTag('');
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!apiService || saving) return;
    setSaving(true);
    setSaved(false);
    setNeedsReload(false);
    setError('');
    let savedOnServer = false;
    try {
      const response = await apiService.updateSequence(sequence.id, {
        survey_type: draft.surveyType,
        personalization: draft.personalization,
        legally_required: draft.legallyRequired,
        internal_name: draft.internalName,
        icon: draft.icon,
        tag_list: draft.tags,
        duration_override: draft.durationOverride
      });
      if (!response?.sequence?.internalSettings) {
        setError(
          response?.errors?.join(' ') ||
            'Unable to save settings. Reload to check the saved values before retrying.'
        );
        return;
      }
      const current = useEditorStore.getState();
      savedOnServer = true;
      if (current.apiService !== apiService) return;
      if (current.training?.id === training?.id) current.markContentEdited();
      if (current.sequence?.id === sequence.id) {
        current.setSequence(response.sequence);
        current.setForceUpdateSequence(true);
      }
      useEditorStore.setState(state => {
        if (
          state.apiService !== apiService ||
          state.training?.id !== training?.id ||
          !state.training
        )
          return state;
        return {
          training: {
            ...state.training,
            chapters: state.training.chapters.map(chapter => ({
              ...chapter,
              chapterSequences: chapter.chapterSequences.map(item =>
                item.sequence.id === sequence.id
                  ? {
                      ...item,
                      sequence: {
                        ...item.sequence,
                        icon: response.sequence.icon,
                        duration: response.sequence.duration
                      }
                    }
                  : item
              )
            }))
          }
        };
      });
      setSaved(true);

      // Read effective compliance metadata from Rails; it can differ from the raw checkbox.
      if (training?.id) {
        const data = await apiService.getCourseInfo(training.id, {
          silent: true
        });
        const authoritative = data?.training?.chapters
          ?.flatMap((chapter: EditorChapter) => chapter.chapterSequences)
          .find(
            (item: EditorChapter['chapterSequences'][number]) =>
              item.sequence.id === sequence.id
          )?.sequence;
        if (authoritative) {
          useEditorStore.setState(state => {
            if (
              state.apiService !== apiService ||
              state.training?.id !== training.id
            )
              return state;
            return {
              training: {
                ...state.training,
                chapters: state.training.chapters.map(chapter => ({
                  ...chapter,
                  chapterSequences: chapter.chapterSequences.map(item =>
                    item.sequence.id === sequence.id
                      ? {
                          ...item,
                          sequence: {
                            ...item.sequence,
                            legallyRequired: authoritative.legallyRequired
                          }
                        }
                      : item
                  )
                }))
              }
            };
          });
        } else {
          setNeedsReload(true);
        }
      }
    } catch {
      if (savedOnServer) setNeedsReload(true);
      else
        setError(
          'Unable to confirm the settings. Reload to check the saved values before retrying.'
        );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay open opacity={0.5} onClick={saving ? undefined : onClose}>
      <Modal
        open
        width="min(440px, calc(100vw - 48px))"
        role="dialog"
        aria-modal="true"
        aria-labelledby="internal-slide-settings-title"
      >
        <Panel
          onKeyDown={event => {
            if (event.key === 'Escape' && !saving) {
              event.stopPropagation();
              onClose();
            }
            if (event.key === 'Tab') {
              const controls = Array.from(
                event.currentTarget.querySelectorAll<HTMLElement>(
                  'select:not(:disabled), input:not(:disabled), button:not(:disabled), summary'
                )
              ).filter(
                control =>
                  control.tagName === 'SUMMARY' ||
                  !control.closest('details:not([open])')
              );
              const first = controls[0];
              const last = controls[controls.length - 1];
              if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last?.focus();
              } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first?.focus();
              }
            }
          }}
        >
          <h3 id="internal-slide-settings-title">Internal slide settings</h3>
          <div className="sequence-id">Sequence ID: {sequence.id}</div>
          <form onSubmit={save}>
            <fieldset disabled={saving}>
              <label className="field">
                Internal name
                <input
                  autoFocus
                  maxLength={255}
                  value={draft.internalName || ''}
                  onChange={event =>
                    change({ internalName: event.target.value })
                  }
                />
              </label>
              <label className="field">
                Menu icon
                <select
                  aria-label="Menu icon"
                  value={draft.icon ?? ''}
                  onChange={event =>
                    change({
                      icon: (event.target.value || null) as Settings['icon']
                    })
                  }
                >
                  {sequence.internalSettings.icon === null && (
                    <option value="">No icon</option>
                  )}
                  <option value="text">Text</option>
                  <option value="video">Video</option>
                  <option value="question">Question</option>
                </select>
              </label>
              <label className="field">
                Manual duration (seconds)
                <input
                  type="number"
                  min={0}
                  max={2147483647}
                  step={1}
                  value={draft.durationOverride ?? ''}
                  onChange={event =>
                    change({
                      durationOverride:
                        event.target.value === ''
                          ? null
                          : Number(event.target.value)
                    })
                  }
                  aria-describedby="internal-duration-help"
                />
              </label>
              <small id="internal-duration-help">
                Leave blank for automatic timing. If media cannot be measured,
                the current duration is retained.
              </small>
              <label className="field">
                Survey type
                <select
                  aria-label="Survey type"
                  value={draft.surveyType || ''}
                  onChange={event =>
                    change({
                      surveyType: (event.target.value ||
                        null) as Settings['surveyType']
                    })
                  }
                >
                  <option value="">Optional (no survey type)</option>
                  <option value="precourse">Pre-course</option>
                  <option value="postcourse">Post-course</option>
                  <option value="custom">Custom</option>
                </select>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={draft.personalization}
                  onChange={event =>
                    change({ personalization: event.target.checked })
                  }
                />
                Apply personalization
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={draft.legallyRequired}
                  onChange={event =>
                    change({ legallyRequired: event.target.checked })
                  }
                />
                Legally required
              </label>
              <small>
                Survey type controls answer tracking and scoring. Legally
                required content may have additional compliance protections.
              </small>
              <details>
                <summary>Tags ({draft.tags.length})</summary>
                <div className="detail-content">
                  {draft.tags.map(tag => (
                    <div key={tag} className="tag">
                      <span>{tag}</span>
                      <button
                        type="button"
                        aria-label={`Remove tag ${tag}`}
                        onClick={() =>
                          change({
                            tags: draft.tags.filter(label => label !== tag)
                          })
                        }
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <label className="field">
                    New tag
                    <input
                      value={newTag}
                      maxLength={255}
                      onChange={event => setNewTag(event.target.value)}
                      onKeyDown={event => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          addTag();
                        }
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={!newTag.trim()}
                    onClick={addTag}
                  >
                    Add tag
                  </button>
                </div>
              </details>
              <details>
                <summary>Used in courses ({draft.usedInCourses.total})</summary>
                <div className="detail-content">
                  <small>Current revisions in this company.</small>
                  {draft.usedInCourses.courses.map(course => (
                    <div key={course.id}>
                      <span>{course.title}</span>{' '}
                      <small>· Revision {course.revision}</small>
                    </div>
                  ))}
                  {!draft.usedInCourses.total && (
                    <small>No current courses use this slide.</small>
                  )}
                  {draft.usedInCourses.total >
                    draft.usedInCourses.courses.length && (
                    <small>
                      Showing the first {draft.usedInCourses.courses.length} of{' '}
                      {draft.usedInCourses.total} courses.
                    </small>
                  )}
                </div>
              </details>
              <div className="actions">
                <button type="submit" disabled={!changed || !apiService}>
                  {saving ? 'Saving…' : 'Save settings'}
                </button>
                <button
                  type="button"
                  disabled={!changed}
                  onClick={() => {
                    setDraft(sequence.internalSettings);
                    setNewTag('');
                    setError('');
                    setSaved(false);
                  }}
                >
                  Cancel
                </button>
                <button type="button" onClick={onClose}>
                  Close
                </button>
              </div>
            </fieldset>
          </form>
          {error && <p role="alert">{error}</p>}
          {saved && (
            <p role="status">
              {needsReload
                ? 'Settings saved. Reload to refresh the compliance warning.'
                : 'Settings saved'}
            </p>
          )}
        </Panel>
      </Modal>
    </Overlay>
  );
};

export const InternalSequenceSettings = ({
  onClose = () => undefined
}: {
  onClose?: () => void;
}) => {
  const { sequence } = useEditorStore();
  if (!sequence?.internalSettings || sequence.editable === false) return null;
  return (
    <SettingsForm
      key={sequence.id}
      onClose={onClose}
      sequence={sequence as EditorSequence & { internalSettings: Settings }}
    />
  );
};
